import { gql } from "@apollo/client";

export const CAMPAIGN_WITH_CALLEE_FIELDS = gql`
  fragment CampaignWithCalleeFields on Campaign {
    id
    calleeFields {
      id
      name
      builtInField
      dataSourceFieldName
      sidebarOrder
    }
  }
`;

export const VAN_SURVEY_QUESTION_FIELDS = gql`
  fragment VanSurveyQuestionFields on VanSurveyQuestion {
    name
    scriptQuestion
    responses {
      surveyResponseId
      name
    }
  }
`;

export const CAMPAIGN_WITH_QUESTIONS = gql`
  ${VAN_SURVEY_QUESTION_FIELDS}
  fragment CampaignWithQuestions on Campaign {
    id
    questions {
      key
      script
      isRichText
      name
      questionType
      answers {
        key
        value
        next
        disposition
        vanIntegrationSettings {
          activistCodeId
          activistCodeData {
            name
          }
          surveyResponseId
          resultCodeId
        }
        patchThruConfig {
          fixedNumber
        }
      }
      next
      vanIntegrationSettings {
        surveyQuestionId
        surveyQuestionData {
          ...VanSurveyQuestionFields
        }
      }
      mobilizeAmericaRsvpSettings {
        eventId
        mobilizeTenantSlug
      }
      sendMessageSettings {
        text
      }
      pollingPlaceLookupSettings {
        lookupTool
      }
    }
  }
`;

export const CAMPAIGN_WITH_TALKING_POINTS = gql`
  fragment CampaignWithTalkingPoints on Campaign {
    id
    enableTalkingPoints
    talkingPoints {
      heading
      text
    }
  }
`;

export const CAMPAIGN_WITH_CALLEE_ADDITIONAL_INFO = gql`
  fragment CampaignWithCalleeAdditionalInfo on Campaign {
    id
    enableCalleeAdditionalInfo
    calleeAdditionalInfo {
      name
      phoneNumber
      homeAddress
      pollingPlace
    }
  }
`;

export const CAMPAIGN_WITH_TRUST_PRODUCT_FIELDS = gql`
  fragment CampaignWithTrustProductFields on Campaign {
    id
    customer_profile_sid
    approved_stir_shaken_sid
    approved_voice_integrity_sid
    approved_branded_calling_sid
    approved_branded_calling_basic_sid
  }
`;

export const VAN_INTEGRATION_FIELDS = gql`
  fragment VanIntegrationFields on VanIntegration {
    id
    name
    applicationName
    hasMyVoters
    hasMyCampaign
    createdAt
    deletedAt
  }
`;

export const JOB_FIELDS = gql`
  fragment JobFields on Job {
    id
    type
    createTime
    startTime
    progress
    status
    args
    result
    errorCode
    errorMessage
    errorDetails
  }
`;

export const ADMIN_FIELDS = gql`
  fragment AdminFields on Admin {
    id
    fullName
    email
    isStaff
    isTenantOwner
    createdAt
    createdBy {
      id
      email
      fullName
      isStaff
    }
    deletedAt
    hasLoggedIn
  }
`;

export const MEMBERSHIP_FIELDS = gql`
  ${ADMIN_FIELDS}
  fragment MembershipFields on SubaccountMembership {
    id
    deletedAt
    role
    admin {
      ...AdminFields
    }
  }
`;

export const OPT_OUT_FIELDS = gql`
  ${ADMIN_FIELDS}
  ${JOB_FIELDS}
  fragment OptOutFields on OptOut {
    id
    phoneNumber
    createdAt
    createdByAdmin {
      ...AdminFields
    }
    createdByJob {
      ...JobFields
      createdByAdmin {
        ...AdminFields
      }
    }
    deletedAt
    deletedByAdmin {
      ...AdminFields
    }
  }
`;

export const SUBACCOUNT_FIELDS = gql`
  fragment SubaccountFields on Subaccount {
    id
    name
    createdAt
    settings {
      callbackNumber
      campaignDefaultAreaCode
      campaignCallerPhoneInAreaCode
      campaignDefaultCallingHours {
        startTime
        endTime
        timezone
      }
      approval_requirement
      voicemail_allowed
      collectCallerEmail
      restrictExports
      customer_profile_sid
      approved_stir_shaken_sid
      approved_voice_integrity_sid
      approved_branded_calling_sid
      approved_branded_calling_basic_sid
      captchaRequired
      defaultProvider
    }
    messagingPhoneNumber
    messagingPhoneNumberStatus
  }
`;

export const TENANT_FIELDS = gql`
  fragment TenantFields on Tenant {
    slug
    name
    featureFlags {
      mobilizeAmericaRsvp
      voicemailDrop
      patchThru
      multiplePasses
      usage
      adminsCanToggleAmd
      adminsCanTogglePhoneOnlyMode
      structuredDispositions
      customTwilioIntegration
      humanDetection
      sidebar
      disableClicker
      watermarkDialing
      alternateDropRate
      provisionUnassignedNumbers
      reuseNumbers
      bulkVanSync
      preventMessagingFromUnregisteredNumbers
    }
    defaultFeatureFlags
    timezoneOptions {
      displayName
      timezone
    }
    countryIso
    isUsingSubaccounts
    defaultSubaccount {
      id
    }
    numberRotationThreshold
    csvUploadLimitMb
    csvUploadLimitRows
    minimumOutgoingNumberPoolSize
    twilioSubaccount {
      id
      name
      apiKey
      accountSid
      deletedAt
    }
    approval_requirement
    voicemail_allowed
    deactivatedAt
    s3NonincrementalRowLimit
    s3IncrementalWindowDays
    onHold
  }
`;

export const TENANT_EXPORT_CONFIG_FIELDS = gql`
  fragment TenantExportConfigFields on TenantExportConfig {
    id
    name
    type
    createdAt
    updatedAt
    lastRunJob {
      id
      createTime
      status
      errorCode
      errorMessage
      errorDetails
    }
    s3Settings {
      bucket
      prefix
      awsRegion
      awsAccessKeyId
      incremental
    }
    bigQuerySettings {
      gcsBucket
      gcsBucketPrefix
      dataset
      addTenantScoping
    }
  }
`;

export const TWILIO_SUBACCOUNTS_FIELDS = gql`
  fragment TwilioSubaccountField on TwilioSubaccount {
    id
    name
    accountSid
    apiKey
    deletedAt
  }
`;
